import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Image as ImageIcon, LayoutGrid, Plus, Upload, X } from "lucide-react";
import { toast } from "react-toastify";
import { handleCategoryCreate, handleCategoryUpdate } from "../../requests/Categories/CategoryService";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const fieldClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none ring-0 focus:border-gray-300 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0";

const parentKeyOf = (item) =>
  item?.category_id ??
  item?.parent_id ??
  item?.parentId ??
  item?.parent_category_id ??
  item?.parent?.id ??
  item?.category?.id ??
  "";

const isSubcategoryRecord = (item) => {
  if (parentKeyOf(item)) return true;
  return String(item?.type || item?.categoryType || "").toLowerCase() === "subcategory";
};

const isInactiveRecord = (item) => {
  const raw = item?.status ?? item?.is_active ?? item?.isActive;
  if (raw === false || raw === 0) return true;
  const status = String(raw ?? "active").toLowerCase();
  return status === "inactive" || status === "hidden" || status === "0" || status === "false";
};

const existingCover = (item) =>
  item?.cover_image_url || item?.cover_image || item?.cover || item?.cover_url || "";

const existingThumbnail = (item) =>
  item?.thumbnail_url || item?.thumbnail || item?.thumb || item?.logo || item?.image || item?.image_url || "";

const readImage = (file) =>
  new Promise((resolve, reject) => {
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      reject(new Error("Use a JPG or PNG image."));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      reject(new Error("Image must be 5MB or smaller."));
      return;
    }
    resolve({ file, preview: URL.createObjectURL(file) });
  });

function UploadTile({ title, preview, emptyLabel, onFile }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold text-gray-800">{title}</p>
      <label className="relative flex h-40 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed border-gray-300 bg-gradient-to-b from-slate-100 to-slate-300/70">
        {preview ? (
          <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center text-slate-400">
            <ImageIcon size={22} strokeWidth={1.5} />
            <span className="mt-2 text-[10px] font-semibold tracking-[0.16em]">{emptyLabel}</span>
          </span>
        )}
        <span className="absolute bottom-3 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-800 shadow-sm">
          <Upload size={12} />
          {preview ? "Replace" : "Upload"}
        </span>
        <input type="file" accept="image/jpeg,image/png" className="sr-only" onChange={onFile} />
      </label>
      <p className="mt-1.5 text-[11px] text-gray-400">JPG or PNG · 5MB max</p>
    </div>
  );
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors focus:outline-none focus-visible:outline-none ${
        checked ? "bg-[#FF6C6C]" : "bg-gray-300"
      }`}
    >
      <span
        className={`h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

const categoryNameOf = (item) => item?.name || item?.categoryName || "Untitled";

const childrenOf = (parentId, categories, category) => {
  const parent = [category, ...categories].find((item) => String(item?.id) === String(parentId));
  const embedded = parent?.subcategories || parent?.sub_categories || parent?.children || [];
  const linked = categories.filter((item) => String(parentKeyOf(item)) === String(parentId));
  const seen = new Set();
  return [...embedded, ...linked].filter((item) => {
    if (!item?.id) return false;
    const key = parentKeyOf(item) != null ? `subcategory-${item.id}` : `category-${item.id}`;
    if (key === `category-${parentId}`) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const EditCategoryModal = ({ isOpen, onClose, onSave, category, categories = [] }) => {
  const [placement, setPlacement] = useState("parent");
  const [parentId, setParentId] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [name, setName] = useState("");
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [active, setActive] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nameInputRef = useRef(null);

  const loadRecord = (record) => {
    setEditingRecord(record || null);
    setName(record ? categoryNameOf(record) : "");
    setCover(null);
    setCoverPreview(record && typeof existingCover(record) === "string" ? existingCover(record) : "");
    setThumbnail(null);
    setThumbnailPreview(
      record && typeof existingThumbnail(record) === "string" ? existingThumbnail(record) : ""
    );
    setActive(record ? !isInactiveRecord(record) : true);
    setFeatured(Boolean(record?.featured || record?.is_featured || record?.isFeatured));
  };

  useEffect(() => {
    if (!isOpen || !category) return;
    const nextPlacement = isSubcategoryRecord(category) ? "subcategory" : "parent";
    setPlacement(nextPlacement);
    setParentId(
      nextPlacement === "subcategory"
        ? String(parentKeyOf(category) || "")
        : ""
    );
    loadRecord(category);
    setCreatingNew(false);
    setError("");
    setSubmitting(false);
  }, [isOpen, category]);

  const parentOptions = categories.filter((item) => item?.id && !isSubcategoryRecord(item));
  if (
    category?.id &&
    !isSubcategoryRecord(category) &&
    !parentOptions.some((item) => String(item.id) === String(category.id))
  ) {
    parentOptions.unshift(category);
  }

  const relatedSubcategories =
    placement === "subcategory" && parentId ? childrenOf(parentId, categories, category) : [];
  const openedAsSubcategory = isSubcategoryRecord(category);
  const editingOpenedSubcategory =
    openedAsSubcategory &&
    editingRecord &&
    String(editingRecord.id) === String(category?.id) &&
    !creatingNew;

  const selectParentForSubcategories = (nextParentId) => {
    setParentId(nextParentId);
    setCreatingNew(false);
    setError("");
    if (openedAsSubcategory && editingRecord && String(editingRecord.id) === String(category?.id)) {
      return;
    }
    const subs = childrenOf(nextParentId, categories, category);
    const openedChild = subs.find((item) => String(item.id) === String(category?.id));
    loadRecord(openedChild || (openedAsSubcategory ? category : null));
  };

  const startNewSubcategory = () => {
    setCreatingNew(true);
    loadRecord(null);
    setError("");
    requestAnimationFrame(() => {
      nameInputRef.current?.focus({ preventScroll: true });
      nameInputRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  };

  const switchPlacement = (nextPlacement) => {
    setError("");
    setPlacement(nextPlacement);
    if (nextPlacement === "parent") {
      const parentRecord = isSubcategoryRecord(category)
        ? parentOptions.find((item) => String(item.id) === String(parentKeyOf(category))) || category
        : category;
      setParentId("");
      setCreatingNew(false);
      loadRecord(parentRecord);
      return;
    }

    const anchorId = isSubcategoryRecord(category)
      ? String(parentKeyOf(category) || category.id)
      : String(category.id);
    selectParentForSubcategories(anchorId);
  };

  const onPick = async (event, apply) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      apply(await readImage(file));
    } catch (pickError) {
      toast.error(pickError.message);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (placement === "subcategory" && !creatingNew && !editingRecord) {
      setError("Select a subcategory to edit.");
      return;
    }
    if (name.trim().length < 3) {
      setError("Category name must be at least 3 characters.");
      return;
    }
    if (creatingNew) {
      if (!parentId) {
        setError("Select the parent category this subcategory sits under.");
        return;
      }
      setError("");
      setSubmitting(true);
      await handleCategoryCreate(
        {
          placement: "subcategory",
          parentId,
          name,
          cover,
          thumbnail,
          active,
          featured,
          subcategories: [],
        },
        { onSave, onClose }
      );
      setSubmitting(false);
      return;
    }
    if (placement === "subcategory" && !parentId) {
      setError("Select the parent category this subcategory sits under.");
      return;
    }
    if (placement === "subcategory" && !editingRecord) {
      setError("Select a subcategory to edit.");
      return;
    }
    if (placement === "subcategory" && String(editingRecord?.id) === String(parentId)) {
      setError("Select a subcategory to edit.");
      return;
    }
    setError("");
    setSubmitting(true);
    const record = editingRecord || category;
    await handleCategoryUpdate(
      {
        record: placement === "subcategory" ? "subcategory" : "category",
        id: record?.id,
        slug: record?.category_slug || record?.categorySlug || category?.category_slug,
        name: name.trim(),
        categoryId: placement === "subcategory" ? parentId : null,
        active,
        featured,
        cover,
        thumbnail,
      },
      { onSave, onClose }
    );
    setSubmitting(false);
  };

  const renderCategoryFields = (nameLabel, placeholder) => (
    <>
      <section className="mt-3 rounded-2xl border border-gray-200 p-3">
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold text-gray-800">{nameLabel}</span>
          <input
            ref={nameInputRef}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={placeholder}
            className={fieldClass}
          />
        </label>
      </section>

      <section className="mt-3 rounded-2xl border border-gray-200 p-3">
        <p className="text-xs font-semibold text-gray-800">Images</p>
        <p className="mb-3 text-[11px] text-gray-400">
          Cover shows on the storefront. Thumbnail shows in lists.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <UploadTile
            title="Cover image"
            emptyLabel="CATEGORY COVER"
            preview={coverPreview}
            onFile={(event) =>
              onPick(event, (image) => {
                if (coverPreview?.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
                setCover(image.file);
                setCoverPreview(image.preview);
              })
            }
          />
          <UploadTile
            title="Thumbnail"
            emptyLabel="LIST THUMBNAIL"
            preview={thumbnailPreview}
            onFile={(event) =>
              onPick(event, (image) => {
                if (thumbnailPreview?.startsWith("blob:")) URL.revokeObjectURL(thumbnailPreview);
                setThumbnail(image.file);
                setThumbnailPreview(image.preview);
              })
            }
          />
        </div>
      </section>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 px-3 py-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-900">Active</p>
            <p className="text-[11px] leading-tight text-gray-400">Visible to shoppers</p>
          </div>
          <Switch checked={active} onChange={setActive} label="Active" />
        </div>
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 px-3 py-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-900">Featured</p>
            <p className="text-[11px] leading-tight text-gray-400">Highlight on storefront</p>
          </div>
          <Switch checked={featured} onChange={setFeatured} label="Featured" />
        </div>
      </div>
    </>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="edit-category-backdrop"
          className="fixed inset-0 z-[1000] bg-black/45"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
      )}
      {isOpen && (
        <motion.form
          key="edit-category-drawer"
          onSubmit={submit}
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="category-drawer fixed inset-y-0 right-0 z-[1001] flex h-full w-full max-w-[440px] flex-col overflow-hidden bg-white shadow-2xl"
        >
          <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3 pt-4">
            <div className="flex items-start gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FFF1F0] text-[#FF6C6C]">
                <LayoutGrid size={16} />
              </span>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  {openedAsSubcategory && placement === "subcategory" ? "Edit subcategory" : "Edit category"}
                </h2>
                <p className="text-xs text-gray-500">
                  {openedAsSubcategory && placement === "subcategory"
                    ? "Update this subcategory on its own"
                    : "Update this category on its own"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X size={16} />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 pb-4">
            <section className="rounded-2xl border border-gray-200 p-3">
              <p className="mb-2 text-xs font-semibold text-gray-800">Placement</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "parent", title: "Parent Category", copy: "A top-level shop section." },
                  { id: "subcategory", title: "Subcategory", copy: "Sits under a parent category." },
                ].map((option) => {
                  const selected = placement === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => switchPlacement(option.id)}
                      className={`rounded-xl px-3 py-2.5 text-left ${
                        selected ? "bg-[#1c2434] text-white" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <span className="block text-xs font-semibold">{option.title}</span>
                      <span className={`mt-0.5 block text-[11px] leading-snug ${selected ? "text-white/75" : "text-gray-400"}`}>
                        {option.copy}
                      </span>
                    </button>
                  );
                })}
              </div>
              {placement === "subcategory" && (
                <label className="mt-3 block">
                  <span className="mb-1.5 block text-xs font-semibold text-gray-800">Sits under</span>
                  <select
                    value={parentId}
                    onChange={(event) => selectParentForSubcategories(event.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Select a parent category</option>
                    {parentOptions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {categoryNameOf(item)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {placement === "subcategory" && parentId && !editingOpenedSubcategory && (
                <div className="mt-3">
                  <p className="mb-1.5 text-[11px] font-semibold text-gray-500">
                    Subcategories under {categoryNameOf(parentOptions.find((item) => String(item.id) === String(parentId)) || category)}
                  </p>
                  {relatedSubcategories.length === 0 ? (
                    <div>
                      <p className="text-xs text-gray-400">This parent category has no subcategories yet.</p>
                      {!creatingNew && (
                        <button
                          type="button"
                          onClick={startNewSubcategory}
                          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 py-2.5 text-xs font-semibold text-gray-600 hover:border-[#FF6C6C] hover:text-[#FF6C6C]"
                        >
                          <Plus size={14} />
                          Add a subcategory
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {relatedSubcategories.map((item) => {
                        const selected = String(editingRecord?.id) === String(item.id);
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              setCreatingNew(false);
                              loadRecord(item);
                              setError("");
                            }}
                            className={`flex w-full items-center rounded-xl border px-3 py-2 text-left text-xs font-semibold ${
                              selected
                                ? "border-[#FF6C6C] bg-[#FFF1F0] text-gray-900"
                                : "border-gray-200 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {categoryNameOf(item)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {(creatingNew || editingRecord) && renderCategoryFields(
                    creatingNew ? "New subcategory" : "Subcategory name",
                    "Subcategory name"
                  )}
                </div>
              )}
            </section>

            {editingOpenedSubcategory && renderCategoryFields("Name", "Subcategory")}

            {placement === "parent" && renderCategoryFields("Name", "Parent Category")}

            {error && <p className="text-xs text-[#E25555]">{error}</p>}
          </div>

          <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-gray-100 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-xl bg-[#FF6C6C] py-2.5 text-sm font-semibold text-white hover:bg-[#ff5252] disabled:opacity-70"
            >
              {submitting ? "Saving..." : creatingNew ? "Create subcategory" : "Save changes"}
            </button>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
};

export default EditCategoryModal;
